'use client';

import { useCallback, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAdMesh } from '../hooks/useAdMesh';
import { logger } from '../utils/logger';
import { AdMeshCompactCard } from './AdMeshCompactCard';
import { AdMeshFollowup } from './AdMeshFollowup';
import type {
  PlatformResponse,
  DelegationActivationPayload,
  ActiveDelegationSession,
  RecommendationReportPayload,
} from '../types/index';

export interface AdMeshRecommendationsProps {
  /** Optional callback when recommendations are shown */
  onRecommendationsShown?: (messageId: string, recommendation: PlatformResponse | null) => void;

  /** Optional callback on error */
  onError?: (error: Error) => void;

  /**
   * Message ID for per-message recommendations.
   *
   * This is the primary identifier for fetching recommendations for a specific message.
   */
  messageId?: string;

  /**
   * User query text for generating recommendations.
   *
   * This is the query that will be used to fetch recommendations from the backend.
   * Typically the user's question or search query.
   *
   * IMPORTANT: This should be a non-empty string. If not provided or empty,
   * the component will skip rendering to prevent 400 Bad Request errors.
   * The backend requires a valid query parameter.
   */
  query?: string;

  /** Legacy bridge CTA callback; prefer onStartDelegation. */
  onPasteToInput?: (content: string) => void;

  /** Called when the SDK needs host approval before starting a delegated brand flow. */
  onDelegationConsent?: (recommendation: PlatformResponse) => boolean | Promise<boolean>;

  /** Called with normalized delegation metadata after consent/engagement. */
  onDelegationActivated?: (payload: DelegationActivationPayload) => void | Promise<void>;

  /** Called after consent is granted so the host can create/open the delegation session. */
  onStartDelegation?: (recommendation: PlatformResponse) => void | Promise<void>;

  /** Called when the user reports a recommendation. */
  onRecommendationReport?: (payload: RecommendationReportPayload) => void | Promise<void>;

  /**
   * Optional container ID for follow-up suggestions.
   * When provided and the recommendation includes a followup_suggestion,
   * the SDK will automatically render the follow-up in this container.
   */
  followups_container_id?: string;

  /**
   * Callback to execute query when follow-up is selected (required for follow-up functionality).
   * When a user clicks on a follow-up suggestion, this callback is invoked with the followup_suggestion.query.
   * This allows the platform to continue the conversation with the sponsored follow-up query.
   */
  onExecuteQuery?: (query: string) => void | Promise<void>;

  /**
   * Callback when a sponsored followup is detected.
   * This allows third-party applications to integrate the sponsored followup query into their own
   * followup suggestions UI (e.g., adding to a suggestions list, related questions section, etc.).
   * 
   * When a user clicks the followup suggestion, the application should:
   * 1. Fire engagement tracking by calling the followupEngagementUrl
   * 2. Execute the followupQuery (e.g., submit it as a new user query)
   * 
   * @param followupQuery - The sponsored followup query text to display to the user
   * @param followupEngagementUrl - The engagement tracking URL to call when user clicks the followup
   * @param recommendationId - The recommendation ID for tracking and correlation
   * 
   * @example
   * ```tsx
   * <AdMeshRecommendations
   *   onFollowupDetected={(query, engagementUrl, recId) => {
   *     // Add to your suggestions list
   *     setSuggestions(prev => [...prev, {
   *       text: query,
   *       sponsored: true,
   *       engagementUrl,
   *       recommendationId: recId
   *     }]);
   *   }}
   * />
   * ```
   */

  onFollowupDetected?: (
    followupSuggestion: any, // Will be updated to proper type later
    recommendationId: string,
  ) => void;

  /**
   * Signal indicating if the followup container is ready in the DOM.
   * 
   * Useful for scenarios where the container is rendered conditionally or after a delay (e.g. streaming).
   * If provided, the component will wait until this is true before attempting to attach the portal.
   */
  isContainerReady?: boolean;

  /**
   * Optional user ID to override the context-provided user ID.
   * Useful for per-message or specific user tracking scenarios.
   */
  userId?: string;

  /**
   * Optional pre-fetched canonical platform result from an upstream backend flow.
   * When provided, the component reuses this payload and skips calling /aip/context.
   */
  initialRecommendation?: PlatformResponse;

  /**
   * Whether the rendered recommendation card should fire its primary exposure URL.
   * Defaults to true. Set false when the recommendation came from a sponsored follow-up click
   * and the click should only count as follow-up engagement.
   */
  triggerExposureUrl?: boolean;
}

/**
 * AdMeshRecommendations - Citation/Product Format Recommendation Display
 *
 * Displays recommendations as a separate UI component. Handles all the complexity:
 * - Uses provided messageId directly
 * - Generates container IDs for recommendations
 * - Calls SDK's showRecommendations()
 *
 * For Weave Ad Format (where AdMesh links are embedded in LLM response),
 * use WeaveAdFormatContainer component instead.
 *
 * @example
 * ```tsx
 * // Per-message recommendations with messageId
 * <AdMeshProvider apiKey={apiKey} sessionId={sessionId}>
 *   {messages.map((msg) => (
 * <div key={msg.messageId}>
 *       {msg.content}
 *       {msg.role === 'assistant' && (
 * <AdMeshRecommendations
 * messageId={msg.messageId}
 *           query={msg.userQuery}
 *         />
 *       )}
 *     </div>
 *   ))}
 * </AdMeshProvider>
 * ```
 *
 * @example
 * ```tsx
 * // Format is auto-detected from brand agent's preferred_format
 * <AdMeshProvider apiKey={apiKey} sessionId={sessionId}>
 *   <Chat messages={messages} />
 *   <AdMeshRecommendations
 *     messageId={lastMessageId}
 *     query="best CRM for small business"
 *   />
 * </AdMeshProvider>
 * ```
 */
export const AdMeshRecommendations = ({
  onRecommendationsShown,
  onError,
  messageId,
  query,
  userId: propUserId,
  initialRecommendation,
  triggerExposureUrl = true,
  followups_container_id,
  onPasteToInput,
  onDelegationConsent,
  onDelegationActivated,
  onStartDelegation,
  onRecommendationReport,
  onExecuteQuery,
  onFollowupDetected,
  isContainerReady,
}: AdMeshRecommendationsProps) => {
  const {
    sdk,
    sessionId,
    apiKey,
    language,
    location,
    userId: contextUserId,
    model,
    messages,
    theme,
    getRecommendationResult,
    hasRecommendationResult,
    setRecommendationResult,
    activeDelegationSession,
    setActiveDelegationSession,
    hasActiveDelegationSession,
  } = useAdMesh();
  const isFilledAuctionResponse = (
    response: PlatformResponse | null | undefined,
  ): response is PlatformResponse => {
    return !!response && response.status === 'generated' && !!response.recommendation;
  };
  
  // Use prop userId if provided, otherwise context userId
  const userId = propUserId || contextUserId;

  const [platformResult, setPlatformResult] = useState<PlatformResponse | null>(null);
  const [detectedFormat, setDetectedFormat] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track fetched messageId to prevent duplicate fetches
  const fetchedMessageIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const reusedInitialRecommendationRef = useRef<boolean>(false);

  // Use refs for callbacks to avoid dependency issues
  const onRecommendationsShownRef = useRef(onRecommendationsShown);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onRecommendationsShownRef.current = onRecommendationsShown;
    onErrorRef.current = onError;
  }, [onRecommendationsShown, onError]);

  // Reset fetched ref when messageId changes
  useEffect(() => {
    if (fetchedMessageIdRef.current !== messageId) {
      fetchedMessageIdRef.current = null;
      reusedInitialRecommendationRef.current = false;
      setPlatformResult(null);
      setDetectedFormat(null);
    }
  }, [messageId]);

  const hasActiveDelegatedSessionForConversation =
    hasActiveDelegationSession() &&
    activeDelegationSession?.session_id === sessionId;

  useEffect(() => {
    if (!messageId || !hasRecommendationResult(messageId)) {
      return;
    }

    if (hasActiveDelegatedSessionForConversation) {
      return;
    }

    const storedResult = getRecommendationResult(messageId);
    if (!storedResult) {
      logger.log('[AdMeshRecommendations] ♻️ Reusing stored no_match result from provider state');
      setPlatformResult(null);
      setDetectedFormat(null);
      setError(null);
      setIsLoading(false);
      fetchedMessageIdRef.current = messageId;
      isFetchingRef.current = false;
      reusedInitialRecommendationRef.current = true;
      return;
    }

    let selectedFormat: string = storedResult.recommendation?.format || 'tail';
    if (selectedFormat === 'weave' || selectedFormat === 'product_card') {
      selectedFormat = 'tail';
    }

    logger.log('[AdMeshRecommendations] ♻️ Reusing stored recommendation result from provider state', {
      messageId,
      recommendationId: storedResult.recommendation_id,
      format: storedResult.recommendation?.format,
    });
    setPlatformResult(storedResult);
    setDetectedFormat(selectedFormat);
    setError(null);
    setIsLoading(false);
    fetchedMessageIdRef.current = messageId;
    isFetchingRef.current = false;
    reusedInitialRecommendationRef.current = true;
    onRecommendationsShownRef.current?.(messageId, storedResult);
  }, [getRecommendationResult, hasRecommendationResult, hasActiveDelegatedSessionForConversation, messageId]);

  // Reuse upstream recommendation payload when available to avoid a second /aip/context call.
  useEffect(() => {
    if (!initialRecommendation || !messageId) {
      return;
    }

    if (hasActiveDelegatedSessionForConversation) {
      return;
    }

    try {
      const canonicalResponse = initialRecommendation;
      const initialStatus = initialRecommendation.status || 'unknown';
      if (!isFilledAuctionResponse(canonicalResponse)) {
        if (initialStatus === 'no_match') {
          logger.log('[AdMeshRecommendations] ℹ️ Initial recommendation returned no_match - rendering nothing');
          setPlatformResult(null);
          setDetectedFormat(null);
          setError(null);
          setIsLoading(false);
          fetchedMessageIdRef.current = messageId;
          isFetchingRef.current = false;
          reusedInitialRecommendationRef.current = true;
          setRecommendationResult(messageId, null);
          return;
        }
        throw new Error(`Initial recommendation is not a filled auction response (status=${initialStatus})`);
      }

      let selectedFormat: string = canonicalResponse.recommendation?.format || 'tail';
      if (selectedFormat === 'weave' || selectedFormat === 'product_card') {
        selectedFormat = 'tail';
      }

      setPlatformResult(canonicalResponse);
      setDetectedFormat(selectedFormat);
      setError(null);
      setIsLoading(false);
      fetchedMessageIdRef.current = messageId;
      isFetchingRef.current = false;
      reusedInitialRecommendationRef.current = true;
      setRecommendationResult(messageId, canonicalResponse);
      onRecommendationsShownRef.current?.(messageId, canonicalResponse);

      logger.log('[AdMeshRecommendations] ♻️ Using initial recommendation payload, skipping /aip/context fetch');
      logger.log('[AdMeshRecommendations] ✅ Accepted initial generated recommendation payload', {
        messageId,
        recommendationId: canonicalResponse.recommendation_id,
        format: canonicalResponse.recommendation?.format,
        brandName: canonicalResponse.recommendation?.creative?.brand_name,
        headline: canonicalResponse.recommendation?.creative?.headline,
        hasTracking: !!canonicalResponse.tracking,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      reusedInitialRecommendationRef.current = false;
      logger.warn(`[AdMeshRecommendations] ⚠️ Failed to reuse initial recommendation, will fall back to fetch: ${error.message}`);
    }
  }, [hasActiveDelegatedSessionForConversation, initialRecommendation, messageId, setRecommendationResult]);

  // Single fetch effect - fetch recommendation once and detect format
  useEffect(() => {
    // Validate required parameters
    if (!messageId || !query || query.trim() === '') {
      logger.log('[AdMeshRecommendations] ❌ Validation failed - missing required parameters');
      setIsLoading(false);
      return;
    }

    if (hasActiveDelegatedSessionForConversation) {
      logger.log('[AdMeshRecommendations] ⏸️ Delegated session active, skipping /aip/context fetch', {
        messageId,
        conversationSessionId: sessionId,
        delegationSessionId: activeDelegationSession?.delegation_session_id,
      });
      setPlatformResult(null);
      setDetectedFormat(null);
      setError(null);
      setIsLoading(false);
      isFetchingRef.current = false;
      return;
    }

    if (!sdk?.fetchPlatformResponse) {
      logger.log('[AdMeshRecommendations] SDK fetchPlatformResponse not available');
      setIsLoading(false);
      return;
    }

    if (reusedInitialRecommendationRef.current) {
      logger.log('[AdMeshRecommendations] ⏭️ Initial recommendation provided, skipping duplicate fetch');
      return;
    }

    // Prevent duplicate fetches for the same messageId
    if (fetchedMessageIdRef.current === messageId) {
      logger.log('[AdMeshRecommendations] ⏭️ Already fetched for this messageId, skipping duplicate fetch');
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      logger.log('[AdMeshRecommendations] ⏭️ Fetch already in progress, skipping duplicate fetch');
      return;
    }

    logger.log('[AdMeshRecommendations] 📤 Fetching recommendation from /aip/context (single fetch)');

    const fetchRecommendations = async () => {
      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setError(null);

        // Single fetch call
        // Note: messages is optional - only pass if available
        const result = await sdk.fetchPlatformResponse({
          query: query.trim(),
          sessionId: sessionId,
          messageId: messageId, // REQUIRED: messageId is mandatory
          language: language,
          location: location,
          userId: userId,
          model: model,
          ...(messages && messages.length > 0 && { messages }), // Optional: only pass if messages exist
        });
        const resultStatus = (result as any)?.status || 'unknown';
        if (!isFilledAuctionResponse(result)) {
          if (resultStatus === 'no_match') {
            logger.log('[AdMeshRecommendations] ℹ️ No recommendation returned from /aip/context');
            setPlatformResult(null);
            setDetectedFormat(null);
            setError(null);
            setIsLoading(false);
            isFetchingRef.current = false;
            fetchedMessageIdRef.current = messageId;
            setRecommendationResult(messageId, null);
            return;
          }
          throw new Error(`No filled recommendation returned from /aip/context (status=${resultStatus})`);
        }

        setPlatformResult(result);
        logger.log('[AdMeshRecommendations] ✅ Accepted fetched generated recommendation payload', {
          messageId,
          recommendationId: result.recommendation_id,
          format: result.recommendation?.format,
          brandName: result.recommendation?.creative?.brand_name,
          headline: result.recommendation?.creative?.headline,
          hasTracking: !!result.tracking,
        });

        let selectedFormat: string = result.recommendation?.format || 'tail';
        if (selectedFormat === 'weave' || selectedFormat === 'product_card') {
          selectedFormat = 'tail';
        }

        setDetectedFormat(selectedFormat);
        fetchedMessageIdRef.current = messageId; // Mark as fetched
        setRecommendationResult(messageId, result);
        logger.log('[AdMeshRecommendations] 📊 Format detection:', {
          preferred_format: result.recommendation?.format,
          selected_format: selectedFormat
        });

        setIsLoading(false);
        isFetchingRef.current = false;
        onRecommendationsShownRef.current?.(messageId, result);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        // Log error but don't break the UI - network errors are expected in some scenarios
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          logger.warn(`[AdMeshRecommendations] ⚠️ Network error fetching recommendations(non - critical): ${error.message} `);
        } else {
          logger.error(`[AdMeshRecommendations] ❌ Error fetching recommendations: ${error.message} `);
        }
        setError(error);
        setIsLoading(false);
        isFetchingRef.current = false;
        onErrorRef.current?.(error);
      }
    };

    fetchRecommendations();
  }, [
    activeDelegationSession?.delegation_session_id,
    hasActiveDelegatedSessionForConversation,
    sdk,
    sessionId,
    messageId,
    query,
    language,
    location,
    userId,
    model,
    initialRecommendation,
    setRecommendationResult,
  ]); // Removed messages, onRecommendationsShown, onError from dependencies

  useEffect(() => {
    if (!platformResult || !onFollowupDetected) {
      return;
    }

    const followupSuggestion =
      platformResult.recommendation?.creative?.follow_up_suggestion &&
      platformResult.tracking?.followup_engagement_url &&
      platformResult.tracking?.followup_exposure_url
        ? {
            label: platformResult.recommendation.creative.follow_up_suggestion,
            query: platformResult.recommendation.creative.follow_up_suggestion,
            engagement_url: platformResult.tracking.followup_engagement_url,
            exposure_url: platformResult.tracking.followup_exposure_url,
          }
        : null;
    if (!followupSuggestion) {
      return;
    }

    onFollowupDetected(followupSuggestion, platformResult.recommendation_id);
  }, [platformResult, onFollowupDetected]);

  const handleSdkDelegationActivated = useCallback(
    async (payload: DelegationActivationPayload) => {
      if (!sdk || !sessionId) {
        throw new Error('AdMesh SDK activation requires an initialized session');
      }

      const response = await fetch(`${sdk.getApiBaseUrl()}/aip/delegation/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          recommendation_id: payload.recommendation_id,
          metadata: payload.metadata,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`Failed to activate delegation session: ${response.status} ${errorBody}`);
      }

      const activation = await response.json();
      const resolvedPayload: DelegationActivationPayload = {
        ...payload,
        delegation_session_id: activation.delegation_session_id || payload.delegation_session_id,
        session_id: activation.session_id || sessionId,
        brand_id: activation.brand_id || payload.brand_id,
        brand_name: activation.brand_name || payload.brand_name,
        mcp_endpoint: activation.mcp_endpoint,
        eligibility_reason: activation.eligibility_reason || payload.eligibility_reason,
        delegate_reason: activation.delegate_reason || payload.delegate_reason,
        session_goal: activation.session_goal || payload.session_goal,
        desired_outcome_type: activation.desired_outcome_type || payload.desired_outcome_type,
        required_user_inputs: activation.required_user_inputs || payload.required_user_inputs || [],
        required_consent_types: activation.required_consent_types || payload.required_consent_types || [],
        success_outcome: activation.success_outcome || payload.success_outcome,
        fallback_message: activation.fallback_message || payload.fallback_message,
        session_mode: 'inline',
        status: activation.status || 'active',
        capabilities: activation.capabilities || payload.capabilities || [],
        allowed_data_types: activation.allowed_data_types || payload.allowed_data_types || [],
        auth_model: activation.auth_model || payload.auth_model,
        handoff_protocol: activation.handoff_protocol || payload.handoff_protocol,
        supported_actions: activation.supported_actions || payload.supported_actions || [],
        metadata: activation.metadata || payload.metadata,
      };

      const activeSession: ActiveDelegationSession = {
        delegation_session_id: activation.delegation_session_id,
        recommendation_id: payload.recommendation_id,
        session_id: activation.session_id || sessionId,
        brand_agent_id: activation.brand_agent_id,
        brand_id: activation.brand_id || payload.brand_id,
        brand_name: activation.brand_name || payload.brand_name || 'Brand',
        mcp_endpoint: activation.mcp_endpoint,
        status: activation.status || 'active',
        activated_at: activation.created_at,
        last_activity_at: activation.updated_at,
        expires_at: activation.expires_at || activation.updated_at,
        eligibility_reason: activation.eligibility_reason || payload.eligibility_reason,
        delegate_reason: activation.delegate_reason || payload.delegate_reason,
        session_goal: activation.session_goal || payload.session_goal,
        desired_outcome_type: activation.desired_outcome_type || payload.desired_outcome_type,
        required_user_inputs: activation.required_user_inputs || payload.required_user_inputs || [],
        required_consent_types: activation.required_consent_types || payload.required_consent_types || [],
        success_outcome: activation.success_outcome || payload.success_outcome,
        fallback_message: activation.fallback_message || payload.fallback_message,
        capabilities: activation.capabilities || payload.capabilities || [],
        allowed_data_types: activation.allowed_data_types || payload.allowed_data_types || [],
        consent_requirements: activation.consent_requirements || payload.consent_requirements || [],
        auth_model: activation.auth_model || payload.auth_model,
        handoff_protocol: activation.handoff_protocol || payload.handoff_protocol,
        supported_actions: activation.supported_actions || payload.supported_actions || [],
        metadata: activation.metadata || payload.metadata,
      };

      setActiveDelegationSession(activeSession);
      await sdk
        .trackPublisherAssistantEvent({
          event_name: 'delegation_started',
          timestamp: new Date().toISOString(),
          session_id: sessionId,
          recommendation_id: payload.recommendation_id,
          delegation_session_id: activation.delegation_session_id,
          brand_name: activeSession.brand_name,
          status: activation.status || 'active',
          metadata: {
            brand_agent_id: activation.brand_agent_id,
            source: 'recommendation_card',
          },
        })
        .catch((analyticsError) => {
          logger.warn('[AdMeshRecommendations] ⚠️ Failed to track delegation_started event:', analyticsError);
        });
      await sdk
        .reportDelegationSessionStarted(activeSession, {
          brand_agent_id: activation.brand_agent_id,
          brand_name: activeSession.brand_name,
        })
        .then((updatedSession) => {
          if (updatedSession) {
            setActiveDelegationSession(updatedSession);
          }
        })
        .catch((eventError) => {
          logger.warn('[AdMeshRecommendations] ⚠️ Failed to report delegation session_started event:', eventError);
        });

      if (onDelegationActivated) {
        try {
          await onDelegationActivated(resolvedPayload);
        } catch (callbackError) {
          logger.warn(
            '[AdMeshRecommendations] ⚠️ Host onDelegationActivated callback failed after successful delegation activation:',
            callbackError,
          );
        }
        return;
      }

      if (onStartDelegation) {
        try {
          await onStartDelegation({
            ...payload.platform_response,
            delegation: {
              ...payload.platform_response.delegation,
              ...resolvedPayload,
              delegation_session_id: activation.delegation_session_id,
              available: true,
              status: activation.status || 'active',
            },
          });
        } catch (callbackError) {
          logger.warn(
            '[AdMeshRecommendations] ⚠️ Host onStartDelegation callback failed after successful delegation activation:',
            callbackError,
          );
        }
      }
    },
    [
      apiKey,
      onDelegationActivated,
      onStartDelegation,
      sdk,
      sessionId,
      setActiveDelegationSession,
    ],
  );

  const handleRecommendationReport = useCallback(
    async (payload: RecommendationReportPayload) => {
      if (onRecommendationReport) {
        await onRecommendationReport(payload);
        return;
      }

      if (!sdk || !sessionId) {
        return;
      }

      await sdk
        .reportSponsoredRecommendation({
          session_id: sessionId,
          message_id: messageId,
          recommendation_id: payload.recommendation_id,
          reason: payload.reason,
          brand_name: payload.recommendation.recommendation?.creative?.brand_name,
          metadata: {
            reason: payload.reason,
            source: 'recommendation_card',
          },
        })
        .catch((analyticsError) => {
          logger.warn('[AdMeshRecommendations] ⚠️ Failed to report sponsored recommendation:', analyticsError);
        });
    },
    [messageId, onRecommendationReport, sdk, sessionId],
  );

  // Don't render anything if validation fails
  if (!messageId || !query || query.trim() === '') {
    return null;
  }

  // Show loading state (optional - can be removed if not needed)
  if (isLoading) {
    return null; // Or return a loading spinner if desired
  }

  // Show error state (optional - can be removed if not needed)
  if (error) {
    return null; // Error is already handled by onError callback
  }

  // Don't render if no recommendation
  if (!platformResult || !detectedFormat) {
    logger.log('[AdMeshRecommendations] 🚫 Nothing to render', {
      messageId,
      hasPlatformResult: !!platformResult,
      detectedFormat,
      isLoading,
      hasError: !!error,
    });
    return null;
  }

  logger.log('[AdMeshRecommendations] 🎨 Rendering recommendation component', {
    messageId,
    recommendationId: platformResult.recommendation_id,
    detectedFormat,
    brandName: platformResult.recommendation?.creative?.brand_name,
    headline: platformResult.recommendation?.creative?.headline,
  });

  const followupContainer =
    typeof document !== 'undefined' &&
    followups_container_id &&
    (isContainerReady ?? true)
      ? document.getElementById(followups_container_id)
      : null;

  return (
    <>
      <div className="admesh-recommendations-container" style={{ marginTop: '1rem' }}>
        <AdMeshCompactCard
          recommendations={[platformResult]}
          theme={theme}
          sessionId={sessionId}
          triggerExposureUrl={triggerExposureUrl}
          onPasteToInput={onPasteToInput}
          onDelegationConsent={onDelegationConsent}
          onDelegationActivated={handleSdkDelegationActivated}
          onStartDelegation={onStartDelegation}
          onRecommendationReport={handleRecommendationReport}
        />
      </div>
      {followupContainer && sdk && sessionId
        ? createPortal(
            <AdMeshFollowup
              recommendation={platformResult}
              theme={theme}
              sdk={sdk}
              sessionId={sessionId}
              onExecuteQuery={onExecuteQuery}
            />,
            followupContainer,
          )
        : null}
    </>
  );
};

export default AdMeshRecommendations;
